import { ACCEPT_TERMS } from "@lib/graphql/mutations";
import type { TermsVersion } from "@lib/types";
import { useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMutation } from "urql";

interface TermsAcceptanceGateProps {
  termsVersion: TermsVersion;
  onAccepted: () => void;
}

export function TermsAcceptanceGate({ termsVersion, onAccepted }: TermsAcceptanceGateProps) {
  const [, acceptTerms] = useMutation(ACCEPT_TERMS);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);

    const result = await acceptTerms({
      input: { termsVersionId: termsVersion.id },
    });

    if (result.error) {
      setError("Something went wrong. Please try again.");
      setAccepting(false);
      return;
    }

    const data = result.data?.acceptTerms;
    if (data?.errors?.length) {
      setError(data.errors[0]);
      setAccepting(false);
      return;
    }

    onAccepted();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="mx-4 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-surface shadow-xl">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-text-primary">{termsVersion.title}</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Please review and accept the terms below to continue.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
              }}
            >
              {termsVersion.content}
            </Markdown>
          </div>
        </div>

        <div className="border-t border-border px-6 py-4">
          {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {accepting ? "Accepting..." : "I Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}
